import { useEffect, useReducer, useRef } from 'react'
import { recurrenceSettingsReducer } from './recurrenceSettingsReducer'
import { useSelector } from 'react-redux'
import { isDeepEqual } from '@/server-data'
import { selectUserConfiguration } from '@/features/domain/user'
import { dateUtils } from '@/utils'
import { createInitialState } from './createInitialState'

export function useRecurrenceSettings(
  fieldValue: uui.domain.ui.forms.RecurrenceSettings,
  onChange: (value: uui.domain.ui.forms.RecurrenceSettings) => void,
) {
  const { today } = useSelector(selectUserConfiguration)
  const todayDate = dateUtils.parseDate(today)

  const [state, dispatch] = useReducer(
    recurrenceSettingsReducer,
    { value: fieldValue, todayDate },
    createInitialState,
  )
  const { value: stateValue, isUpdatingFromProps } = state

  // make onChange stable to avoid unnecessary re-renders
  const api = useRef({
    onChange,
  })
  useEffect(() => {
    api.current.onChange = onChange
  }, [onChange])

  // update the field value when the state changes
  useEffect(() => {
    // if the state is being updated from props, do not trigger onChange
    // to avoid infinite loops
    if (isUpdatingFromProps) return

    // if the state value is deep equal to the field value, do not trigger onChange
    if (isDeepEqual(stateValue, fieldValue)) return
    api.current.onChange(stateValue)
  }, [stateValue, isUpdatingFromProps])

  useEffect(() => {
    dispatch({ type: 'forceUpdate', value: fieldValue })
  }, [fieldValue])

  return {
    state: stateValue,
    dispatch,
  }
}
